(function (root) {
  'use strict';
  if (root.MNL_CART) return;
  var STORAGE_KEY = 'mnl-cart-add-pending-v1';
  var active = false;
  var pending = null;
  function error(code, detail, status) {
    var value = new Error(detail || code);
    value.code = code;
    value.status = status || 0;
    value.uncertain = code === 'uncertain' || code === 'review_previous';
    return value;
  }
  function route(resource) {
    var base = root.Shopify && root.Shopify.routes && root.Shopify.routes.root || '/';
    return base.replace(/\/?$/, '/') + resource;
  }
  function properties(value) {
    var result = {};
    Object.keys(value || {}).sort().forEach(function (key) {
      if (value[key] !== null && value[key] !== undefined && String(value[key]) !== '') result[key] = String(value[key]);
    });
    return result;
  }
  function plan(item) {
    return String(item.selling_plan || (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan && item.selling_plan_allocation.selling_plan.id) || '');
  }
  function identity(item) {
    return JSON.stringify([String(item.variant_id || item.id), Number(item.quantity), plan(item), properties(item.properties)]);
  }
  function loadPending() {
    if (pending) return pending;
    try {
      var raw = root.sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var record = JSON.parse(raw);
      if (!record || record.version !== 1 || !record.item || !record.attempt || !record.fingerprint) throw error('uncertain');
      pending = record;
      return record;
    } catch (_) { throw error('uncertain', 'A previous cart request cannot be verified.'); }
  }
  function persist(record) {
    try {
      var raw = JSON.stringify(record);
      root.sessionStorage.setItem(STORAGE_KEY, raw);
      if (root.sessionStorage.getItem(STORAGE_KEY) !== raw) throw error('storage_unavailable');
      pending = record;
    } catch (_) { throw error('storage_unavailable', 'Cart safety storage is unavailable.'); }
  }
  function clearPending() {
    try { root.sessionStorage.removeItem(STORAGE_KEY); } catch (_) {}
    pending = null;
  }
  function request(url, options, timeout) {
    var controller = typeof root.AbortController === 'function' ? new root.AbortController() : null;
    var timer;
    return new Promise(function (resolve, reject) {
      timer = root.setTimeout(function () {
        if (controller) controller.abort();
        reject(error('timeout'));
      }, timeout || 15000);
      var init = Object.assign({ credentials: 'same-origin', cache: 'no-store', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } }, options || {});
      if (controller) init.signal = controller.signal;
      Promise.resolve().then(function () { return root.fetch(url, init); }).then(function (response) {
        return response.json().catch(function () { return null; }).then(function (body) {
          if (!response.ok) throw error('http', body && (body.description || body.message) || 'HTTP ' + response.status, response.status);
          resolve(body);
        });
      }).catch(reject);
    }).finally(function () { root.clearTimeout(timer); });
  }
  function read() {
    return request(route('cart.js')).then(function (cart) {
      if (!cart || !Array.isArray(cart.items) || cart.items.some(function (item) { return !item || !Number.isInteger(item.quantity) || item.quantity < 0; })) throw error('invalid_cart');
      return cart;
    });
  }
  function matching(item, expected) {
    return item && identity(item) === identity(expected);
  }
  function validAdded(body, item) {
    return body && Array.isArray(body.items) && body.items.length === 1 && matching(body.items[0], item);
  }
  function sameLine(item, expected) {
    return identity(Object.assign({}, item, { quantity: 0 })) === identity(Object.assign({}, expected, { quantity: 0 }));
  }
  function quantityConfirmed(cart, expected) {
    if (!cart || !Array.isArray(cart.items)) return false;
    var matches = cart.items.filter(function (item) { return sameLine(item, expected); });
    return expected.quantity === 0 ? matches.length === 0 : matches.length === 1 && matches[0].quantity === expected.quantity;
  }
  function reconcile(record, fingerprint) {
    return read().then(function (cart) {
      if (record.kind === 'quantity') {
        if (!quantityConfirmed(cart, record.item)) throw error('uncertain');
        clearPending();
        if (fingerprint !== record.fingerprint) throw error('review_previous');
        return cart;
      }
      var matches = cart.items.filter(function (item) { return item.properties && item.properties._mnl_add_attempt === record.attempt; });
      if (matches.length !== 1 || !matching(matches[0], record.item)) throw error('uncertain', 'Cart result remains uncertain.');
      clearPending();
      if (fingerprint !== record.fingerprint) throw error('review_previous', 'The previous addition was confirmed; review the cart before adding another formula.');
      return { items: matches, recovered: true };
    }).catch(function (cause) {
      if (cause.code === 'review_previous') throw cause;
      throw error('uncertain', 'Check the cart before attempting another purchase.');
    });
  }
  function add(payload) {
    if (active) return Promise.reject(error('busy'));
    active = true;
    return Promise.resolve().then(function () {
      if (!payload || !Array.isArray(payload.items) || payload.items.length !== 1) throw error('invalid_request');
      var item = Object.assign({}, payload.items[0]);
      item.properties = properties(item.properties);
      delete item.properties._mnl_add_attempt;
      if (!/^[1-9]\d*$/.test(String(item.id)) || !Number.isInteger(item.quantity) || item.quantity < 1) throw error('invalid_request');
      if (root.MNL_RECIPE_GUARD) root.MNL_RECIPE_GUARD.assertAddItem(item);
      else if ((root.MNL_RECIPE_CONFIG && root.MNL_RECIPE_CONFIG.variantIds || []).some(function(id){ return String(id) === String(item.id); })) throw error('recipe_required', 'The composition check is unavailable; nothing was submitted.');
      var fingerprint = identity(item);
      var previous = loadPending();
      if (previous) return reconcile(previous, fingerprint);
      return read().catch(function () { throw error('preflight', 'The cart could not be checked; nothing was submitted.'); }).then(function () {
        var attempt = root.crypto && typeof root.crypto.randomUUID === 'function' ? root.crypto.randomUUID() : Date.now().toString(36) + '-' + Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
        item.properties._mnl_add_attempt = attempt;
        var record = { version: 1, kind: 'add', attempt: attempt, fingerprint: fingerprint, item: item, startedAt: Date.now() };
        persist(record);
        var body = Object.assign({}, payload, { items: [item] });
        return request(route('cart/add.js'), { method: 'POST', body: JSON.stringify(body) }, 30000).then(function (result) {
          if (!validAdded(result, item)) throw error('invalid_success');
          clearPending();
          return result;
        }).catch(function (cause) {
          if (cause.status >= 400 && cause.status < 500 && cause.status !== 408 && cause.status !== 429) {
            return read().then(function (cart) {
              var matches = cart.items.filter(function (line) { return line.properties && line.properties._mnl_add_attempt === record.attempt; });
              if (!matches.length) { clearPending(); throw cause; }
              if (matches.length === 1 && matching(matches[0], record.item)) { clearPending(); return { items: matches, recovered: true }; }
              throw error('uncertain', 'The rejected request may have partially changed the cart.');
            }, function () { throw error('uncertain', 'The rejected request could not be reconciled.'); });
          }
          return reconcile(record, fingerprint);
        });
      });
    }).finally(function () { active = false; });
  }
  function change(key, quantity) {
    if (active) return Promise.reject(error('busy'));
    active = true;
    return Promise.resolve().then(function () {
      if (!key || !Number.isInteger(quantity) || quantity < 0) throw error('invalid_request');
      var fingerprint = 'quantity:' + String(key) + ':' + quantity;
      var previous = loadPending();
      if (previous) return reconcile(previous, fingerprint);
      return read().then(function (cart) {
        var line = cart.items.find(function (item) { return item.key === key; });
        if (!line) { var missing = error('line_missing'); missing.cart = cart; throw missing; }
        if (line.quantity === quantity) return cart;
        var expected = Object.assign({}, line, { quantity: quantity });
        var record = { version: 1, kind: 'quantity', attempt: 'quantity', fingerprint: fingerprint, item: expected, startedAt: Date.now() };
        persist(record);
        return request(route('cart/change.js'), { method: 'POST', body: JSON.stringify({ id: key, quantity: quantity }) }).then(function (result) {
          if (!result || !Array.isArray(result.items) || result.items.some(function (item) { return !item || !Number.isInteger(item.quantity) || item.quantity < 0; })) throw error('invalid_success');
          clearPending();
          if (!quantityConfirmed(result, expected)) { var refused = error('quantity_refused'); refused.cart = result; throw refused; }
          return result;
        }).catch(function (cause) {
          if (cause.cart || cause.status >= 400 && cause.status < 500 && cause.status !== 408 && cause.status !== 429) {
            clearPending();
            throw cause;
          }
          return reconcile(record, fingerprint);
        });
      });
    }).finally(function () { active = false; });
  }
  function recover() {
    if (active) return Promise.reject(error('busy'));
    active = true;
    return Promise.resolve().then(function () {
      var record = loadPending();
      return record ? reconcile(record, record.fingerprint) : read();
    }).finally(function () { active = false; });
  }
  root.MNL_CART = { add: add, change: change, read: read, recover: recover, isBusy: function () { return active; }, hasPending: function () { try { return !!loadPending(); } catch (_) { return true; } }, cartUrl: function () { return route('cart'); } };
}(window));

